<template>
    <el-table
        v-loading="status == 'loading'"
        :data="datas"
        style="width: 100%"
        empty-text="沒有資料"
        class="accounting-summary-table accounting-summary-invoice-table"
        :height="maxHeight"
        border
        @selection-change="$emit('selected', $event)"
        @row-click="onRowClick"
    >
        <el-table-column type="selection" width="42" fixed></el-table-column>
        <el-table-column label="系統單號" width="97" align="left" fixed>
            <template slot-scope="scope">
                <span @click="($event) => onClick($event, scope.row.sno)" style="font-size: 8.5pt">{{ scope.row.uniqueNumber }}</span>
            </template>
        </el-table-column>
        <el-table-column label="財會檢核" align="left" width="80" fixed>
            <template slot-scope="scope">
                <AccountingStatusLabel :statusNum="scope.row.vocherStatus" />
            </template>
        </el-table-column>
        <el-table-column label="核准傳票" width="76" class-name="strong" align="center" fixed>
            <template slot-scope="scope">
                <div :class="{ 'blur-zero': scope.row.approvedVocherQty === 0 }">{{ scope.row.approvedVocherQty }}</div>
            </template>
        </el-table-column>
        <el-table-column label="待辦傳票" width="76" class-name="strong" align="center" fixed>
            <template slot-scope="scope">
                <div :class="{ 'blur-zero': scope.row.vocherQty - scope.row.approvedVocherQty === 0 }">{{ scope.row.vocherQty - scope.row.approvedVocherQty }}</div>
            </template>
        </el-table-column>
        <el-table-column prop="summary" label="摘要" min-width="300" class-name="strong" fixed show-overflow-tooltip />
        <el-table-column prop="iteM_NAM" label="收入項目" min-width="130" class-name="strong" show-overflow-tooltip />
        <el-table-column label="憑證類型" prop="typeName" width="110">
            <template slot-scope="scope">
                <span> {{ scope.row.typeName }} </span>
                <span v-if="scope.row.tranInvoiceCount > 0">(已轉開)</span>
            </template>
        </el-table-column>
        <el-table-column label="發票號碼" prop="foo" width="115" class-name="strong" show-overflow-tooltip>
            <template slot-scope="scope">
                <div v-if="scope.row.typeName === '核銷'" style="font-family: monospace">{{ getTranInvoiceListText(scope.row.tranInvoiceList) }}</div>
                <div v-else style="font-family: monospace">{{ scope.row.foo }}</div>
            </template>
        </el-table-column>
        <el-table-column label="發票開立日" width="100" align="center">
            <template slot-scope="scope">
                <div>{{ scope.row.foO_DAT | dateFormater("YYYY-MM-dd") }}</div>
            </template>
        </el-table-column>
        <el-table-column prop="non-tax-amt" label="銷售額(未稅)" min-width="100" class-name="blue strong" align="right">
            <template slot-scope="scope">
                <Money :v="scope.row.amount - scope.row.amT_TAX" :precision="scope.row.cny === 'TWD' ? null : 4" />
            </template>
        </el-table-column>
        <el-table-column prop="amT_TAX" label="稅金" min-width="80" class-name="blue strong" align="right">
            <template slot-scope="scope">
                <Money :v="scope.row.amT_TAX" :precision="scope.row.cny === 'TWD' ? null : 4" />
            </template>
        </el-table-column>
        <el-table-column prop="amount" label="含稅總計" min-width="100" class-name="blue strong" align="right">
            <template slot-scope="scope">
                <Money :v="scope.row.amount" :precision="scope.row.cny === 'TWD' ? null : 4" />
            </template>
        </el-table-column>
        <el-table-column prop="cny" label="幣別" class-name="blue" min-width="70" align="center" />
        <el-table-column prop="rat" label="匯率" class-name="blue" min-width="75" align="right" />
        <el-table-column prop="amount" label="(最新) 入帳方式" class-name="yellow" min-width="130" align="center">
            <template slot-scope="scope"> {{ getTransferTypeText(scope.row.incomeTransferType) }} </template>
        </el-table-column>
        <el-table-column prop="incomeCount" label="入帳筆數" class-name="yellow" min-width="75" align="center" />
        <el-table-column prop="incomeBnkDat" label="(最新) 入帳日期" class-name="yellow" min-width="110" align="center">
            <template slot-scope="scope">
                <div v-if="scope.row.incomeBnkDat">{{ scope.row.incomeBnkDat | dateFormater("YYYY-MM-dd") }}</div>
                <span v-else> -- </span>
            </template>
        </el-table-column>
        <el-table-column prop="incomeBnkDat" label="入帳總額" class-name="yellow strong" min-width="100" align="right">
            <template slot-scope="scope">
                <Money :v="scope.row.incomeTotalAmount" :precision="scope.row.cny === 'TWD' ? null : 4" />
            </template>
        </el-table-column>
        <el-table-column prop="incomeBnkNam" label="(最新) 銀行帳戶" class-name="yellow" min-width="120" align="center" show-overflow-tooltip>
            <template slot-scope="scope">
                <span>{{ scope.row.incomeBnkNam || "--" }}</span>
            </template>
        </el-table-column>
        <el-table-column prop="foO_DIS_DAT" label="折讓日期" class-name="red" min-width="110" align="center">
            <template slot-scope="scope">
                <span v-if="scope.row.foO_DIS">{{ scope.row.foO_DIS_DAT | dateFormater("YYYY-MM-dd") }}</span>
                <span v-else> -- </span>
            </template>
        </el-table-column>
        <el-table-column prop="foO_DIS_AMT" label="折讓金額" class-name="red strong" min-width="110" align="right">
            <template slot-scope="scope">
                <Money v-if="scope.row.foO_DIS" :v="scope.row.foO_DIS_AMT" :precision="scope.row.cny === 'TWD' ? null : 4" />
                <span v-else> -- </span>
            </template>
        </el-table-column>
        <el-table-column label="建立日期" width="120" align="center">
            <template slot-scope="scope">
                <span>{{ scope.row.crT_DAT | dateFormater("YYYY-MM-dd") }}</span>
            </template>
        </el-table-column>
    </el-table>
</template>

<script>
import AccountingStatusLabel from '@/components/dashboard/Accounting/AccountingSummary/ui/AccountingStatusLabel.vue';
import AccountingTableMixins from '../AccountingTableMixins.vue';

export default {
    mixins: [AccountingTableMixins],
    props: {
        status: {
            type: String,
            default: '',
        },
        datas: {
            type: Array,
            default: () => [],
        },
    },
    components: { AccountingStatusLabel },
    methods: {
        onClick(e, sno) {
            e.preventDefault();
            e.stopPropagation();
            this.$emit('openReceipt', sno);
        },
        onRowClick(data) {
            this.$emit('openDetail', data.sno, data.vocherStatus);
        },
        getTransferTypeText(type) {
            switch (type) {
            case 0:
                return '銀行匯款';
            case 1:
                return '收現金存銀行';
            case 2:
                return '收現金存零用金';
            default:
                return '--';
            }
        },
        getTranInvoiceListText(tranInvoiceList) {
            return tranInvoiceList.map((o) => o.foo).join(', ');
        },
    },
};
</script>
